import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@/hooks/global-tools';
import { useApprovalPassportSearch } from '@/hooks/apiHooks';
import { addApprovalReferrers } from '@/hooks/approval/useApprovalMentionPermission';
import type { ApprovalChatShareRequest } from '@/utils/approval/approvalChat';
import {
  ShareHint,
  ShareTarget,
  ShareTargets,
  SuggestionAsk,
  SuggestionButton,
  SuggestionButtons,
  SuggestionCard as Card,
  SuggestionComment,
  SuggestionResolved,
} from './styles';

/** 모델이 「정민채 과장님」처럼 호칭을 붙여 보내도 이름만으로 찾는다 */
const toSearchName = (raw: string) =>
  raw
    .trim()
    .replace(/\s*(님|씨)$/, '')
    .split(/\s+/)[0] ?? '';

type TargetPickerProps = {
  name: string;
  selectedId: string | null;
  disabled: boolean;
  onSelect: (name: string, passportId: string | null) => void;
};

/** 받는 사람 한 명 — 이름으로 전자결재 인물검색, 동명이인이면 고르게 한다 */
const TargetPicker = ({
  name,
  selectedId,
  disabled,
  onSelect,
}: TargetPickerProps) => {
  const { t } = useTranslation();
  const keyword = toSearchName(name);
  const { data, isLoading } = useApprovalPassportSearch(
    { keyword, listSize: 10, activated: 1 },
    2,
  );
  const matches = useMemo(
    () => (data?.data ?? []).filter((p) => p.detail.name === keyword),
    [data, keyword],
  );

  // 딱 한 명이면 바로 선택, 없거나 여럿이면 비워 둔다(여럿은 사용자가 고른다)
  useEffect(() => {
    if (isLoading) return;
    if (matches.length === 1) {
      if (selectedId !== matches[0].passportId) {
        onSelect(name, matches[0].passportId);
      }
    } else if (
      selectedId &&
      !matches.some((p) => p.passportId === selectedId)
    ) {
      onSelect(name, null);
    }
  }, [isLoading, matches, name, onSelect, selectedId]);

  if (isLoading) {
    return <ShareTarget>{t('찾는 중…')}</ShareTarget>;
  }
  if (matches.length === 0) {
    return (
      <ShareTarget missing>
        {name} — {t('이름으로 찾지 못했습니다')}
      </ShareTarget>
    );
  }
  const label = (p: (typeof matches)[number]) =>
    [
      p.detail.name,
      p.detail.positionName,
      p.detail.organizationDisplayName || p.detail.departmentName,
    ]
      .filter(Boolean)
      .join(' · ');

  if (matches.length === 1) {
    return <ShareTarget>{label(matches[0])}</ShareTarget>;
  }
  return (
    <ShareTarget>
      <span>{t('동명이인이 있어 선택해 주세요')}</span>
      {matches.map((p) => (
        <label key={p.passportId}>
          <input
            type="radio"
            name={`share-${keyword}`}
            checked={selectedId === p.passportId}
            disabled={disabled}
            onChange={() => onSelect(name, p.passportId)}
          />
          {label(p)}
        </label>
      ))}
    </ShareTarget>
  );
};

type Props = {
  messageId: string;
  documentId: string;
  share: ApprovalChatShareRequest;
  /** 문서에 참조인을 추가할 수 있는지(상태 P·H·C·R, 비공개 9 아님) — 결재 차례와는 무관 */
  canShare: boolean;
  done: boolean;
  onDone: () => void;
  /** 전달할 준비가 되면 실행 함수를, 아니면 null 을 알린다 — 같은 답변의 승인·반려 카드가 먼저 전달하도록 */
  onReadyChange: (
    messageId: string,
    run: (() => Promise<boolean>) | null,
  ) => void;
};

/**
 * 「○○에게 전달해」 확인 카드 (PORTAL-15736). 받는 사람을 이 문서의 참조인으로 추가하고 참조 메일을 보낸다 —
 * 결재 상세의 「참조인 추가」와 같은 API. 실제 추가는 「전달」을 눌러야 한다.
 */
const ShareCard = ({
  messageId,
  documentId,
  share,
  canShare,
  done,
  onDone,
  onReadyChange,
}: Props) => {
  const { t } = useTranslation();
  const { showSuccess, showErrorMessage } = useSnackbar();
  const [selected, setSelected] = useState<Record<string, string | null>>({});
  const [submitting, setSubmitting] = useState(false);

  const handleSelect = useCallback(
    (name: string, passportId: string | null) =>
      setSelected((prev) =>
        prev[name] === passportId ? prev : { ...prev, [name]: passportId },
      ),
    [],
  );

  const passportIds = useMemo(
    () =>
      share.names
        .map((n) => selected[n])
        .filter((id): id is string => Boolean(id)),
    [selected, share.names],
  );
  const ready = canShare && !done && passportIds.length === share.names.length;

  const run = useCallback(async () => {
    if (!ready || submitting) return false;
    setSubmitting(true);
    try {
      await addApprovalReferrers(
        documentId,
        Array.from(new Set(passportIds)),
        share.message,
      );
      showSuccess({ message: t('참조인 설정과 참조메일이 발송되었습니다.') });
      onDone();
      return true;
    } catch (e) {
      showErrorMessage(e);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [
    documentId,
    onDone,
    passportIds,
    ready,
    share.message,
    showErrorMessage,
    showSuccess,
    submitting,
    t,
  ]);

  useEffect(() => {
    onReadyChange(messageId, ready ? run : null);
    return () => onReadyChange(messageId, null);
  }, [messageId, onReadyChange, ready, run]);

  if (done) {
    return (
      <Card>
        <SuggestionResolved accepted>✓ {t('전달했습니다')}</SuggestionResolved>
      </Card>
    );
  }
  if (!canShare) {
    return (
      <Card>
        <SuggestionResolved accepted={false}>
          {t('이 문서는 참조인을 추가할 수 없어 전달할 수 없습니다.')}
        </SuggestionResolved>
      </Card>
    );
  }

  return (
    <Card>
      <SuggestionAsk>
        {t('아래 사람을 이 문서의 참조인으로 추가해 전달할까요?')}
      </SuggestionAsk>
      <ShareTargets>
        {share.names.map((name) => (
          <TargetPicker
            key={name}
            name={name}
            selectedId={selected[name] ?? null}
            disabled={submitting}
            onSelect={handleSelect}
          />
        ))}
      </ShareTargets>
      {share.message && (
        <SuggestionComment>
          <b>{t('전달 메시지')}</b>
          {share.message}
        </SuggestionComment>
      )}
      <ShareHint>{t('참조인으로 추가되고 참조 메일이 발송됩니다.')}</ShareHint>
      <SuggestionButtons>
        <SuggestionButton
          type="button"
          primary
          disabled={!ready || submitting}
          onClick={() => void run()}
        >
          {t('전달')}
        </SuggestionButton>
      </SuggestionButtons>
    </Card>
  );
};

export default ShareCard;
